import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { usePaymentConfirmation } from '../../hooks/usePaymentConfirmation';
import repositoryContainer from '../../services/RepositoryContainer';
import ClientHeaderNavigation from '../../components/ui/ClientHeaderNavigation';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const BookingPaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { companyPath } = useParams();
  const reference = searchParams.get('reference') || searchParams.get('trxref');
  const [pendingBooking] = useState(() => {
    try {
      const stored = localStorage.getItem(`clawfe:pendingBooking:${companyPath}`);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const sourceReferenceCode = pendingBooking?.referenceCode;

  // 'verifying' | 'waiting' | 'confirmed' | 'failed'
  const [stage, setStage] = useState('verifying');
  const [copiedReference, setCopiedReference] = useState(false);
  const verifyAttempted = useRef(false);

  // Once confirmed, read the saved context data and navigate to confirmation page
  useEffect(() => {
    if (stage !== 'confirmed') return;

    const redirect = async () => {
      // Brief pause so the success animation is visible
      await new Promise(res => setTimeout(res, 1500));

      let bookingConfirmation = null;
      try {
        const stored = localStorage.getItem(`clawfe:pendingBooking:${companyPath}`);
        if (stored) {
          const pending = JSON.parse(stored);
          if (!pending?.paymentReference || pending.paymentReference === reference) {
            bookingConfirmation = pending;
            localStorage.removeItem(`clawfe:pendingBooking:${companyPath}`);
          }
        }
      } catch {}

      // Fallback: just the reference code — confirmation page still renders the reference card
      if (!bookingConfirmation) {
        bookingConfirmation = { referenceCode: reference, confirmationNumber: reference };
      }

      navigate(`/${companyPath}/booking-confirmation`, {
        state: { bookingConfirmation },
        replace: true,
      });
    };

    redirect();
  }, [stage]);

  // Try to verify immediately (card / Apple Pay)
  useEffect(() => {
    if (!reference || verifyAttempted.current) return;
    verifyAttempted.current = true;

    const attemptVerify = async () => {
      try {
        await repositoryContainer.booking.verifyPayment(reference);
        if (sourceReferenceCode) {
          await repositoryContainer.booking.getByReferenceCode(sourceReferenceCode).catch(() => null);
        }
        setStage('confirmed');
      } catch {
        // Likely mobile money — switch to waiting for SignalR
        setStage('waiting');
      }
    };

    attemptVerify();
  }, [reference]);

  // Mobile money — listen for backend webhook confirmation via SignalR
  const { status: signalRStatus, timeoutReached } = usePaymentConfirmation(
    stage === 'waiting' ? reference : null,
    async () => {
      if (sourceReferenceCode) {
        await repositoryContainer.booking.getByReferenceCode(sourceReferenceCode).catch(() => null);
      }
      setStage('confirmed');
    },
  );

  useEffect(() => {
    if (signalRStatus === 'failed') setStage('failed');
  }, [signalRStatus]);

  const handleCopyReference = async () => {
    if (!reference) return;

    try {
      await navigator?.clipboard?.writeText(reference);
      setCopiedReference(true);
      window.setTimeout(() => setCopiedReference(false), 2000);
    } catch (error) {
      console.error('Failed to copy reference:', error);
    }
  };

  if (!reference) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center space-y-4 max-w-sm">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
            <Icon name="AlertCircle" size={32} className="text-destructive" />
          </div>
          <h2 className="font-heading font-bold text-xl text-foreground">Invalid Payment Link</h2>
          <p className="text-muted-foreground text-sm">No payment reference found. Please contact support.</p>
          <button
            onClick={() => navigate(`/${companyPath}`)}
            className="text-sm text-primary underline underline-offset-4"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <ClientHeaderNavigation />

      <main className="flex items-center justify-center min-h-[calc(100vh-80px)] px-4">
        <div className="max-w-sm w-full text-center space-y-6">

          {/* ── Verifying ── */}
          {stage === 'verifying' && (
            <>
              <div className="relative mx-auto w-20 h-20">
                <div className="absolute inset-0 rounded-full border-4 border-primary/20" />
                <div className="w-20 h-20 rounded-full border-4 border-primary border-t-transparent animate-spin" />
              </div>
              <div className="space-y-2">
                <h2 className="font-heading font-bold text-2xl text-foreground">Verifying Payment</h2>
                <p className="text-muted-foreground text-sm">Confirming your payment, just a moment…</p>
              </div>
            </>
          )}

          {/* ── Waiting (mobile money) ── */}
          {stage === 'waiting' && (
            <>
              <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
                <Icon name="Smartphone" size={36} className="text-amber-600" />
              </div>
              <div className="space-y-2">
                <h2 className="font-heading font-bold text-2xl text-foreground">Approve on Your Phone</h2>
                <p className="text-muted-foreground text-sm">
                  Check your phone for the Mobile Money prompt and approve to complete your booking.
                </p>
              </div>

              {timeoutReached ? (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3 text-left">
                  <p className="text-sm font-semibold text-amber-800">Taking longer than expected</p>
                  <p className="text-xs text-amber-700">
                    If you approved the payment, your booking is likely confirmed. Check your email or use the reference below.
                  </p>
                  <p className="font-mono text-xs font-bold text-amber-900 bg-amber-100 rounded-lg px-3 py-2 text-center">
                    {reference}
                  </p>
                  <Button
                    size="sm"
                    variant={copiedReference ? 'success' : 'outline'}
                    iconName={copiedReference ? 'Check' : 'Copy'}
                    onClick={handleCopyReference}
                    className="w-full"
                  >
                    {copiedReference ? 'Copied!' : 'Copy reference'}
                  </Button>
                  <button
                    onClick={() => navigate(`/${companyPath}`)}
                    className="w-full text-xs text-amber-800 underline underline-offset-2"
                  >
                    Back to Home
                  </button>
                </div>
              ) : (
                <div className="bg-card border border-border rounded-xl p-4 space-y-2">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                    <span className="text-xs text-amber-700 font-medium">Listening for approval…</span>
                  </div>
                  <p className="text-xs text-muted-foreground font-mono">{reference}</p>
                  <Button
                    size="sm"
                    variant={copiedReference ? 'success' : 'outline'}
                    iconName={copiedReference ? 'Check' : 'Copy'}
                    onClick={handleCopyReference}
                    className="w-full"
                  >
                    {copiedReference ? 'Copied!' : 'Copy reference'}
                  </Button>
                </div>
              )}
            </>
          )}

          {/* ── Confirmed — briefly shown while navigating ── */}
          {stage === 'confirmed' && (
            <>
              <div className="relative mx-auto w-20 h-20">
                <div className="absolute inset-0 rounded-full bg-green-100 animate-ping opacity-40" />
                <div className="relative w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                  <svg className="w-9 h-9 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <div className="space-y-2">
                <h2 className="font-heading font-bold text-2xl text-foreground">Payment Confirmed!</h2>
                <p className="text-muted-foreground text-sm">Taking you to your booking details…</p>
              </div>
              <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <div className="w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                Redirecting
              </div>
            </>
          )}

          {/* ── Failed ── */}
          {stage === 'failed' && (
            <>
              <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
                <Icon name="XCircle" size={40} className="text-destructive" />
              </div>
              <div className="space-y-2">
                <h2 className="font-heading font-bold text-2xl text-foreground">Payment Failed</h2>
                <p className="text-muted-foreground text-sm">
                  We couldn't confirm your payment. Please try again or contact support.
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => navigate(-1)}
                  className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 transition-opacity"
                >
                  Try Again
                </button>
                <button
                  onClick={() => navigate(`/${companyPath}`)}
                  className="w-full py-3 rounded-xl border border-border text-foreground font-semibold text-sm hover:bg-muted transition-colors"
                >
                  Back to Home
                </button>
              </div>
            </>
          )}

        </div>
      </main>
    </div>
  );
};

export default BookingPaymentCallback;
